import { FormFieldWrapper, WrappedInputProps } from '@/common/components/Form/FormFieldWrapper.tsx'
import React from 'react'
import { useController, useWatch } from 'react-hook-form'
import { cn } from '@/utils/style.ts'
import { InputNumber, Select, Space } from 'antd'
import type { Money } from '@/common/types/misc.def.ts'
import { useFormSection } from '@/common/components/Form/useFormSection.tsx'

type FormMoneyInputProps = {
  placeholder?: string
  disabled?: boolean
  currencyOptions: string[]
} & WrappedInputProps

export const FormMoneyInput: React.FC<FormMoneyInputProps> = ({
  name,
  label,
  description,
  placeholder,
  className,
  wrapperClassName,
  disabled,
  currencyOptions,
}) => {
  const { fieldName } = useFormSection(name)
  const { field } = useController({ name: fieldName })
  const fieldValue = useWatch({ name: fieldName }) as Money | null

  const currency = fieldValue?.currency || currencyOptions[0]
  const amount = fieldValue?.amount

  const onAmountChange = (value: string | number | null) => {
    if (value === null) {
      field.onChange(undefined)
    } else {
      field.onChange({ currency, amount: value } as Money)
    }
  }

  const onCurrencyChange = (value: string | number | null) => {
    field.onChange({ currency: value, amount } as Money)
  }

  const CurrencySelector = (
    <Select value={currency} onChange={onCurrencyChange} className="w-20" disabled={disabled}>
      {currencyOptions.map(x => <Select.Option key={x} value={x}>{x}</Select.Option>)}
    </Select>
  )

  return (
    <FormFieldWrapper
      className={wrapperClassName}
      name={name}
      label={label}
      disabled={disabled}
      description={description}
    >
      <Space.Compact className="w-full">
        {CurrencySelector}
        <InputNumber
          className={cn('w-full', className)}
          size="middle"
          placeholder={placeholder}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
          value={amount?.toString() ?? ''}
          onChange={onAmountChange}
          disabled={disabled}
        />
      </Space.Compact>
    </FormFieldWrapper>
  )
}
